/**
 * The ability bar's, the cast bar's and the status rows' words (P2.2-T4; ADR-061's copy rules), and the words of the
 * ability and effect tooltips, which are drafted from the rules tables (P2.2-T12, ADR-079's amendment of 2026-10-02
 * "tooltips"): every line is built from an ability's or an effect's record and the constants, never written per
 * record, so a record added to the tables gets its tooltip with no wording of its own. Every word is a draft for the
 * author. `move` is a slot's description of how to move it from the keyboard (ADR-121 decision 3.3).
 *
 * A number is handed in as the text the page prints (`{amount}`, `{count}`, `{page}`, `{percent}` and the rest). A
 * time (`{time}`, `{window}`, `{immunity}`) and a length (`{range}`, `{length}`, `{radius}`) arrive worded, as "1.5 s"
 * and "3 tiles". `lastBlowVerbs` and `lastBlowApplies` are the
 * openings of a chain's effect lines, which land from its last blow alone: each is written whole, beside the
 * opening it stands in for (`controlVerbs`, `applies`). `chainAmounts`, `thenStep` and `either` join a list two
 * parts at a time.
 */
export const BAR = {
  bar: "Abilities",
  empty: "Empty slot",
  own: "Your effects",
  target: "Target's effects",
  cast: "Cast bar",
  move: "Shift+Enter to move",
  slot: "{name}, key {key}",
  slotCounted: "{name}, {count}, key {key}",
  swap: "Swap ability page: {page} of {pages}, slots {first} to {last}, key {key}",
  slotTouch: "{name}",
  slotCountedTouch: "{name}, {count}",
  swapTouch: "Swap ability page: {page} of {pages}, slots {first} to {last}",
  refused: {
    dead: "Dead",
    stunned: "Stunned",
    bar: "Not on your bar",
    cooldown: "Not ready",
    cost: "Cannot pay",
    target: "No target",
    view: "Out of view",
    range: "Out of range",
    sight: "No line of sight",
    peace: "Cannot harm",
    busy: "Already acting",
    passage: "Safe passage",
    targetPassage: "Target in safe passage",
    held: "Paused",
    targetHeld: "With a Game Master",
    other: "Refused",
  },
  blows: {
    basic: "basic blow",
    heavy: "heavy blow",
  },
  controlVerbs: {
    stun: "Stuns",
  },
  lastBlowVerbs: {
    stun: "The last blow stuns",
  },
  limitVerbs: {
    move: "move",
    act: "act",
  },
  blowCounts: {
    n1: "One",
    n2: "Two",
    n3: "Three",
    n4: "Four",
    n5: "Five",
    n6: "Six",
    n7: "Seven",
    n8: "Eight",
  },
  chainDamage: "{count} blows of {amounts} {type} (base). Each lands only on what is in front of you at the time.",
  chainAmounts: "{first} and {last}",
  unlessBlocked: ", unless it is fully blocked",
  applies: "Applies",
  readyIn: "Ready in",
  lastBlowApplies: "The last blow applies",
  effectFor: "{opening} {effect} for {time}{unless}.",
  controlFor: "{opening} for {time}{unless}.",
  diminishing: "{effect} again within {window}: {steps}. Every later one lasts a quarter.",
  thenStep: "{before}, then {after}",
  weaponBlow: "{weapon}, {blow}",
  everyCharacter: "Every character",
  ability: "Ability",
  shape: {
    target: "Hits your target within {range}.",
    ally: "Aims at you, or an ally within {range}.",
    self: "Lands on you.",
    dash: "Dashes {length} over {time} the way you move, or the way you face; stops at a wall.",
    projectile: "Throws a shot up to {range} that bursts where it lands, hitting {who} within {radius}.",
    cone: "Hits {who} in a {degrees}° cone ahead.",
    line: "Hits {who} in a line {length} ahead.",
  },
  nearest: "the nearest",
  upTo: "up to {count}",
  rangeSelf: "Self",
  rooted: "You cannot move or act for {time}.",
  deals: "Deals {amount} {type} damage (base).",
  chance: "{percent}% chance on a hit to apply {effect} for {time}.",
  timeRange: "{shortest} to {longest}",
  removes: "Removes {effect}.",
  unpaid: "Without enough {resource} it costs nothing and deals less damage.",
  cost: "{amount} {resource}",
  modifiers: {
    damageDealt: "Damage dealt {percent}",
    damageTaken: "Damage taken {percent}",
    maxHealth: "Maximum health {percent}",
    focusRegeneration: "Focus regeneration {percent}",
    moveSpeed: "Movement speed {percent}",
    cooldown: "Cooldowns {percent}",
    absorb: "Absorbs {amount} damage",
  },
  cannot: "Cannot {verbs}.",
  either: "{first} or {second}",
  passage: "Players cannot attack you, and you cannot attack them.",
  effectTips: {
    drenched: "Dries out of the rain; faster indoors or by a fire.",
  },
  periodic: "Deals {amount} {type} damage every {time}.",
  appliedBy: "Applied by {abilities}.",
  buff: "Buff",
  debuff: "Debuff",
} as const;
